import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import {
  C, WEEK_DAYS, DEMO_AI_FEED, DEMO_ATTENTION_ITEMS,
  filledSlots, totalSlots, openSlots, copaysCollected, copaysOutstanding,
  copaysPendingCount, noShowRiskCount, revenueAtRisk, estRevenue,
  avgWait, waitingCount, pColor,
} from './frontDeskData';

export const OverviewView = ({ patients, goCalendar, selectPatient, openPayment, advanceStage }) => (
  <div data-testid="fd-overview">
    {/* Financial health bar */}
    <div style={{ background: '#fff', borderBottom: '.5px solid #ebebeb', padding: '14px 20px', display: 'flex', gap: 20 }} data-testid="fd-financial-bar">
      {[
        { label: 'Slots filled today', value: `${filledSlots} of ${totalSlots}`, color: filledSlots >= totalSlots ? C.green : C.black, sub: openSlots > 0 ? `${openSlots} open \u00b7 AI has 3 waitlist matches` : 'All slots filled', subColor: openSlots > 0 ? C.amber : C.green },
        { label: 'Copays collected', value: `$${copaysCollected}`, color: copaysCollected >= 100 ? C.green : C.amber, sub: copaysOutstanding > 0 ? `$${copaysOutstanding} outstanding \u00b7 ${copaysPendingCount} patients` : 'All collected', subColor: copaysOutstanding > 0 ? C.amber : C.green },
        { label: 'No-show risk', value: `${noShowRiskCount} patients`, color: noShowRiskCount > 2 ? C.red : noShowRiskCount > 0 ? C.amber : C.green, sub: `Echo AI sent ${noShowRiskCount * 2} extra reminders`, subColor: '#555' },
        { label: 'Avg wait time', value: `${avgWait} min`, color: avgWait <= 10 ? C.green : avgWait <= 20 ? C.amber : C.red, sub: `${waitingCount} patients waiting now`, subColor: '#555' },
      ].map((c, i) => (
        <div key={i} style={{ flex: 1 }} data-testid={`fd-stat-${i}`}>
          <p style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>{c.label}</p>
          <p style={{ fontSize: 22, fontWeight: 500, color: c.color }}>{c.value}</p>
          <p style={{ fontSize: 9, color: c.subColor }}>{c.sub}</p>
        </div>
      ))}
    </div>

    {/* Two-column layout */}
    <div style={{ display: 'flex', minHeight: 0 }}>
      {/* LEFT: Schedule */}
      <div style={{ flex: 6, padding: '14px 16px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: C.black }}>Today's schedule</span>
          <button onClick={goCalendar} style={{ fontSize: 11, color: C.purple, background: 'none', border: 'none', cursor: 'pointer' }} data-testid="fd-view-calendar">View calendar &rarr;</button>
        </div>
        {patients.map(p => {
          const borderColor = p.stage === 'checked_out' ? '#d1d5db' : p.noShowRisk >= 25 ? C.red : p.aiStatus.type === 'insurance_flagged' ? C.red : p.copay.amount > 0 && !p.copay.collected ? C.amber : C.green;
          return (
            <div key={p.id} onClick={() => selectPatient(p.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '.5px solid #f5f5f5', borderLeft: `3px solid ${borderColor}`, cursor: 'pointer', opacity: p.stage === 'checked_out' ? 0.5 : 1, background: '#fff', borderRadius: 2 }}
              data-testid={`fd-schedule-row-${p.id}`}>
              <div style={{ width: 60, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: C.black }}>{p.time}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.black }}>{p.name}</span>
                <p style={{ fontSize: 10, color: '#aaa' }}>{p.type} &middot; {p.provider}</p>
                <p style={{ fontSize: 10, color: p.aiStatus.color }}>
                  {p.aiStatus.type === 'confirmed' ? '\u2713' : p.aiStatus.type === 'no_show_risk' ? '\u26a0' : p.aiStatus.type === 'insurance_flagged' ? '\u2715' : '\u2192'} AI: {p.aiStatus.text}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {p.copay.amount > 0 && !p.copay.collected && (
                  <button onClick={e => { e.stopPropagation(); openPayment(p.id); }}
                    style={{ fontSize: 10, color: C.amber, background: '#fffbeb', border: '.5px solid #fde68a', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontWeight: 500 }}
                    data-testid={`fd-copay-badge-${p.id}`}>${p.copay.amount} due</button>
                )}
                {(p.stage === 'scheduled' || p.stage === 'arrived') && p.stage !== 'checked_out' && (
                  <button onClick={e => { e.stopPropagation(); advanceStage(p.id); }}
                    style={{ height: 26, padding: '0 10px', borderRadius: 4, background: C.greenBtn, color: '#fff', fontSize: 10, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                    data-testid={`fd-checkin-btn-${p.id}`}>Check In</button>
                )}
              </div>
            </div>
          );
        })}

        {/* Revenue recovery strip */}
        {openSlots > 0 && (
          <div style={{ background: '#fafafa', border: '.5px solid #ebebeb', borderRadius: 8, padding: '8px 12px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }} data-testid="fd-recovery-strip">
            <Sparkles style={{ width: 14, height: 14, color: C.purple, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 11, color: '#555' }}>Echo AI has <strong>3 waitlist patients</strong> ready to fill today's {openSlots} open slot{openSlots > 1 ? 's' : ''}</span>
            <span style={{ fontSize: 10, color: C.amber, padding: '1px 6px', border: '.5px solid #fde68a', borderRadius: 4, background: '#fffbeb' }}>{openSlots} open</span>
            <button onClick={() => toast.success('AI auto-filling open slots')}
              style={{ height: 26, padding: '0 10px', borderRadius: 5, background: C.black, color: '#fff', fontSize: 10, fontWeight: 500, border: 'none', cursor: 'pointer' }}
              data-testid="fd-autofill-btn">Auto-fill with AI</button>
          </div>
        )}

        {/* 7-day calendar strip */}
        <div style={{ marginTop: 16, background: '#fff', border: '.5px solid #ebebeb', borderRadius: 8, padding: '10px 14px' }} data-testid="fd-week-strip">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>This week</span>
            <button onClick={goCalendar} style={{ fontSize: 10, color: C.purple, background: 'none', border: 'none', cursor: 'pointer' }}>View full calendar &rarr;</button>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {WEEK_DAYS.map((d, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 6, background: d.isToday ? C.black : 'transparent', color: d.isToday ? '#fff' : '#555', cursor: 'pointer', position: 'relative' }}
                onClick={goCalendar} data-testid={`fd-weekday-${i}`}>
                <p style={{ fontSize: 9, opacity: 0.7 }}>{d.day}</p>
                <p style={{ fontSize: 13, fontWeight: 500 }}>{d.date}</p>
                <p style={{ fontSize: 9, opacity: 0.6 }}>{d.appointments}</p>
                {d.hasRisk && <span style={{ position: 'absolute', top: 2, right: 2, width: 4, height: 4, borderRadius: '50%', background: C.red }} />}
                {d.hasOpen && !d.hasRisk && <span style={{ position: 'absolute', top: 2, right: 2, width: 4, height: 4, borderRadius: '50%', background: C.amber }} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT: AI Intelligence Panel */}
      <div style={{ flex: 4, borderLeft: '.5px solid #ebebeb', background: '#fff', padding: '14px 16px', overflowY: 'auto' }}>
        {/* Echo AI Live Feed */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Sparkles style={{ width: 13, height: 13, color: C.purple }} />
            <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>Echo AI</span>
            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#f0fdf4', color: C.green, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: C.green, animation: 'pulse 2s infinite' }} />Live
            </span>
          </div>
          {DEMO_AI_FEED.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: i < DEMO_AI_FEED.length - 1 ? '.5px solid #f5f5f5' : 'none' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: a.color, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 10, color: '#555' }}>{a.icon} {a.text}</span>
              <span style={{ fontSize: 9, color: '#aaa', flexShrink: 0 }}>{a.time}</span>
            </div>
          ))}
          <button onClick={() => toast.info('Opening Comms Hub')} style={{ fontSize: 10, color: C.purple, background: 'none', border: 'none', cursor: 'pointer', marginTop: 6 }}>View all in Comms Hub &rarr;</button>
        </div>

        {/* Needs your attention */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>Needs your attention</span>
            <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: DEMO_ATTENTION_ITEMS.length > 0 ? '#fffbeb' : '#f0f0f0', color: DEMO_ATTENTION_ITEMS.length > 0 ? C.amber : '#aaa', fontWeight: 600 }}>{DEMO_ATTENTION_ITEMS.length}</span>
          </div>
          {DEMO_ATTENTION_ITEMS.map((item, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: '.5px solid #f5f5f5', display: 'flex', alignItems: 'flex-start', gap: 8 }} data-testid={`fd-attention-${i}`}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: pColor(item.priority), flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 500, color: C.black }}>{item.patient}</p>
                <p style={{ fontSize: 10, color: '#555' }}>{item.issue}</p>
                <p style={{ fontSize: 9, color: '#aaa' }}>{item.financial}</p>
              </div>
              <button onClick={() => { if (item.action.startsWith('Collect')) openPayment(item.pid); else { selectPatient(item.pid); toast.info(item.action); } }}
                style={{ fontSize: 10, color: '#555', background: '#f5f5f5', border: '.5px solid #e8e8e8', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', flexShrink: 0, fontWeight: 500 }}
                data-testid={`fd-attention-action-${i}`}>{item.action}</button>
            </div>
          ))}
        </div>

        {/* AI Financial Predictions */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 500, color: C.black, marginBottom: 6 }}>AI forecast &middot; today</p>
          {[
            { label: 'Expected no-shows', value: '1\u20132', note: `Based on ${noShowRiskCount * 3} signals`, color: '#555' },
            { label: 'Revenue at risk from no-shows', value: `$200\u2013$400`, note: '', color: C.amber },
            { label: 'Projected copays collected', value: `$${copaysCollected + copaysOutstanding}`, note: '', color: C.green },
            { label: 'Slots that can still be filled', value: `${openSlots} open`, note: '', color: C.purple },
          ].map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '.5px solid #f5f5f5', fontSize: 11, color: '#555' }}>
              <span>{r.label}</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontWeight: 500, color: r.color }}>{r.value}</span>
                {r.note && <p style={{ fontSize: 9, color: '#aaa' }}>{r.note}</p>}
              </div>
            </div>
          ))}
          <p style={{ fontSize: 9, color: '#aaa', fontStyle: 'italic', marginTop: 6 }}>Updated every 5 minutes</p>
        </div>
      </div>
    </div>
  </div>
);
